import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const categoryInfo = article ? categories.find((c) => c.slug === article.category) : null;

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到" description="抱歉，您访问的文章不存在。" />
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm font-medium">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '友田真希官方粉丝应援会',
    },
    image: article.coverImage,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://makidotcom.github.io/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://makidotcom.github.io/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        schema={articleSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-20">
        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryInfo?.name || article.category, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-xs px-3 py-1 rounded-full bg-primary-50 text-primary-600 whitespace-nowrap">
                {categoryInfo?.name || article.category}
              </span>
              <span className="text-sm text-foreground-500">{article.date}</span>
              <span className="text-sm text-foreground-500">·</span>
              <span className="text-sm text-foreground-500">阅读时间 {article.readTime} 分钟</span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <p className="text-sm text-foreground-600">{article.author}</p>
          </div>

          <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`友田真希 ${article.title}`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed mb-10"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex items-center gap-2 mb-10 flex-wrap">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-secondary-50 text-secondary-700 text-xs rounded-full border border-secondary-200"
              >
                #{tag}
              </span>
            ))}
          </div>

          {relatedArticles.length > 0 && (
            <div className="border-t border-background-200/70 pt-10">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关文章</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="w-full h-40 overflow-hidden">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        title={`友田真希 ${related.title}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-foreground-500 mb-1">{related.date}</p>
                      <h4 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {related.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}